import { Stack, useToast } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { lazy } from '@loadable/component';
import { SuspendWithSpinner } from 'components/SuspendWithSpinner';
import { OverlayProps, TaskOverlayBody } from 'components/TaskOverlay';
import { FirstStepTaskOverlay } from 'components/TaskOverlay/FirstStepTaskOverlay';
import React, { useEffect, useState } from 'react';
import { PayloadError } from 'relay-runtime';
import { RemirrorJSON } from 'remirror';
import { EmptyRemirrorJson, isEmptyMessage } from 'utils/editor';
import { graphql, useMutation, useSafeRelay } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { useFeatureFlag } from 'utils/sessionProvider';
import { useFileUploader } from 'utils/upload';
import Links from '../AnnouncementFormLinks';
import AttachmentUploadButton from '../AttachmentUploadButton';
import { AttachmentUploadItem } from '../AttachmentUploadItem';
import { EditAnnouncementOverlay_fetchAnnouncmentQuery_Query } from './__generated__/EditAnnouncementOverlay_fetchAnnouncmentQuery_Query.graphql';
import { EditAnnouncementOverlay_updateAnnouncement_Mutation } from './__generated__/EditAnnouncementOverlay_updateAnnouncement_Mutation.graphql';

const RichTextArea = lazy(() => import('components/Editor/RichTextArea'));

const fetchAnnouncmentQuery = graphql`
  query EditAnnouncementOverlay_fetchAnnouncmentQuery_Query($id: ID!) {
    announcement: node(id: $id) {
      ... on Announcement {
        __typename
        id
        message
        canEdit: canUpdate {
          value
        }
        space {
          id
          modules {
            ... on SpaceAnnouncementsModule {
              canCreateAnnouncement {
                value
              }
            }
          }
        }
        links {
          nodes {
            id
            item {
              __typename
              ... on AnnouncementAttachment {
                content {
                  __typename
                  ... on File {
                    name
                    mimeType
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const updateAnnouncementMutation = graphql`
  mutation EditAnnouncementOverlay_updateAnnouncement_Mutation(
    $announcementId: ID!
    $message: String!
    $shouldAddLinks: Boolean!
    $newLinks: [AnnouncementLinkInput!]!
    $shouldDeleteLinks: Boolean!
    $linkIdsToDelete: [ID!]!
  ) {
    addLinksToAnnouncement(
      input: { announcementId: $announcementId, links: $newLinks }
    ) @include(if: $shouldAddLinks) {
      announcementUpdated {
        id
      }
      addedLinks {
        id
      }
    }
    removeLinksFromAnnouncement(
      input: { announcementId: $announcementId, linkIds: $linkIdsToDelete }
    ) @include(if: $shouldDeleteLinks) {
      announcementUpdated {
        id
      }
    }
    updateAnnouncement(
      input: { announcementId: $announcementId, message: $message }
    ) {
      announcementUpdated {
        id
        ...AnnouncementCard_announcement
      }
    }
  }
`;

interface EditAnnouncementOverlayProps extends OverlayProps {
  announcementId: string;
}

export const EditAnnouncementOverlay = ({
  isOpen,
  onClose,
  announcementId,
}: EditAnnouncementOverlayProps) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const [fileState, setFileState] = useState<UploadState[]>([]);

  const { announcement } =
    useLazyLoadQuery<EditAnnouncementOverlay_fetchAnnouncmentQuery_Query>(
      fetchAnnouncmentQuery,
      { id: announcementId },
    );

  if (announcement?.__typename !== 'Announcement') {
    throw new Error('Could not find the announcement to update');
  }

  const [message, setMessage] = useState<RemirrorJSON>(
    announcement.message as RemirrorJSON ?? EmptyRemirrorJson
  );
  const toast = useToast();

  const [updateAnnouncement, updateAnnouncementLoading] =
    useMutation<EditAnnouncementOverlay_updateAnnouncement_Mutation>(
      updateAnnouncementMutation,
    );

  const handleError = (error: PayloadError | Error) => {
    toast({
      status: 'error',
      title: t`Unable to update the announcement`,
      description: error.message || t`Unknown reason`,
    });
  };

  const handleUpdate = () => {
    const linkIdsToDelete = fileState
      .filter((u) => u.linkId && u.deleted)
      .map((u) => u.linkId as string);
    const shouldDeleteLinks = !!linkIdsToDelete.length;

    const newLinks = fileState
      .filter((u) => u.uploadToken && !u.deleted)
      .map(({ uploadToken }) => ({
        attachment: { uploadToken: uploadToken as string },
      }));
    const shouldAddLinks = !!newLinks.length;

    updateAnnouncement({
      variables: {
        announcementId: announcementId,
        message: JSON.stringify(message),
        shouldAddLinks,
        newLinks,
        shouldDeleteLinks,
        linkIdsToDelete,
      },
      payloadErrorBehaviour: 'HANDLED',
      onError: handleError,
      onCompleted: onClose,
    });
  };

  const messageChangeHandler = (stringifiedMessage?: string) => {
    const message = JSON.parse(stringifiedMessage || '');
    if (isEmptyMessage(message)) {
      setMessage(EmptyRemirrorJson);
    } else {
      setMessage(message);
    }
  };

  // This is a fully working guared to only select nodes with a content File
  // However, I have no idea how to tell Typescript this, so it will complain
  //   below when the values are used. So below I bail out, and use an explicit any typing.
  const fileAttachments = (announcement.links.nodes || [])
    .filter(
      (n) =>
        n?.item?.__typename === 'AnnouncementAttachment' &&
        n?.item.content?.__typename === 'File',
    )
    .filter(notEmpty);

  const linkIds = (announcement.links?.nodes || [])
    .map((n) => n?.id)
    .filter(notEmpty);

  const shouldAllowAnnouncementsWithAttachments = useFeatureFlag(
    'FEATURE_ANNOUNCEMENTS_WITH_ATTACHMENTS',
  );

  const allowAttachments =
    fileAttachments.length || shouldAllowAnnouncementsWithAttachments;

  const existingAttachments = fileAttachments.map((fileAttachment) => ({
    key: fileAttachment.id as string,
    linkId: fileAttachment.id as string,
    name: (fileAttachment.item as any)?.content?.name as string,
    type: (fileAttachment.item as any)?.content?.mimeType as string,

    localFile: undefined,
    uploadToken: undefined,
    uploadCompleted: false,
    deleted: false,
  }));

  const notDeletedFiles = fileState.filter((u) => !u.deleted);
  const attachmentsHaveChanged = notDeletedFiles.length !== existingAttachments.length || notDeletedFiles.some((file) => {
    return file.localFile || !existingAttachments.some((existingFile) => existingFile.linkId === file.linkId)
  });

  const isEmpty = isEmptyMessage(message) && notDeletedFiles.length === 0;
  const hasChanged = JSON.stringify(announcement.message) !== JSON.stringify(message)
    || attachmentsHaveChanged

  const disallowUpdate = isEmpty || !hasChanged;

  return (
    <FirstStepTaskOverlay
      title={t`Announcement`}
      submitButtonTitle={t`Update`}
      isOpen={isOpen}
      onClose={onClose}
      isLoading={updateAnnouncementLoading}
      isDisabled={disallowUpdate}
      onCompleteTask={handleUpdate}
    >
      <TaskOverlayBody my={4}>
        <Stack>
          <SuspendWithSpinner>
            <RichTextArea
              autoFocus
              placeholder={t`Text here`}
              onChange={messageChangeHandler}
              editable
              spaceId={announcement.space.id}
              data={announcement.message as RemirrorJSON}
            />
          </SuspendWithSpinner>

          {allowAttachments ? (
            <AnnouncementAttachmentsManager
              onChange={setFileState}
              existingAttachments={existingAttachments}
            />
          ) : (
            <Links linkIds={linkIds} />
          )}
        </Stack>
      </TaskOverlayBody>
    </FirstStepTaskOverlay>
  );
};

interface UploadState {
  key: string;
  linkId: string | undefined;
  name: string;
  type: string;

  localFile: undefined | File;
  uploadCompleted: boolean;
  uploadToken: string | undefined;
  deleted: boolean;
}

const AnnouncementAttachmentsManager = ({
  onChange,
  existingAttachments,
}: {
  onChange: (uploadState: UploadState[]) => void;
  existingAttachments?: UploadState[];
}) => {
  const [selectedFiles, setSelectedFiles] = useState<UploadState[]>(
    existingAttachments || [],
  );

  useEffect(() => {
    onChange(selectedFiles);
  }, [JSON.stringify(selectedFiles)]);

  const { uploadFile } = useFileUploader('FILES', {
    maxNumberOfFiles: 100,
    minNumberOfFiles: 1,
  });

  const handleSelectedFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) {
      return;
    }
    Array.from(e.target.files).forEach((file) => {
      const localFileURL = URL.createObjectURL(file);
      setSelectedFiles((s) => [
        ...s,
        {
          key: Date.now() + localFileURL,
          name: file.name,
          type: file.type,

          localFile: file,
          uploadToken: undefined,
          uploadCompleted: false,
          linkId: undefined,
          deleted: false,
        },
      ]);
      uploadFile(file).then(([uppyFile, fileFromUppy]) => {
        setSelectedFiles((s) =>
          s.map((u) => {
            if (u.localFile === fileFromUppy) {
              return {
                ...u,
                uploadToken: uppyFile.meta.uploadToken as string,
                uploadCompleted: true,
              };
            } else {
              return u;
            }
          }),
        );
      });
    });
  };

  const handleRemoveLink = (linkId: string) => {
    setSelectedFiles((s) =>
      s.map((u) => ({
        ...u,
        deleted: u.linkId === linkId || u.uploadToken === linkId || u.deleted,
      })),
    );
  };

  return (
    <Stack>
      <Stack>
        {selectedFiles
          .filter(({ deleted }) => !deleted)
          .map((upload) => (
            <AttachmentUploadIndicator
              key={upload.key}
              upload={upload}
              handleRemoveLink={handleRemoveLink}
            />
          ))}
      </Stack>
      <AttachmentUploadButton onChange={handleSelectedFile} />
    </Stack>
  );
};

const AttachmentUploadIndicator = ({
  upload,
  handleRemoveLink,
}: {
  upload: UploadState;
  handleRemoveLink: (linkId: string) => void;
}) => (
  <AttachmentUploadItem
    uploading={!upload.uploadToken && !upload.linkId}
    name={upload.name}
    mimeType={upload.type}
    handleRemove={() =>
      handleRemoveLink((upload.linkId || upload.uploadToken) as string)
    }
  />
);
